import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../../api/axiosConfig';
import {
  HiOutlineCheck,
  HiOutlinePencil,
  HiOutlinePlus,
  HiOutlineSparkles,
  HiOutlineTrash,
  HiOutlineX
} from 'react-icons/hi';

function parseLocalDate(value) {
  if (!value) return '';
  if (value instanceof Date) return value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    const [year, month, day] = value.slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(value);
}

function formatDateInput(value) {
  const date = parseLocalDate(value);
  if (!date) return '';
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDisplayDate(value) {
  const date = parseLocalDate(value);
  if (!date) return '-';
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function getWeekNumber(value) {
  const source = parseLocalDate(value);
  if (!source) return getWeekNumber(new Date());
  const safeSource = Number.isNaN(source.getTime()) ? new Date() : source;
  const d = new Date(Date.UTC(safeSource.getFullYear(), safeSource.getMonth(), safeSource.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function getWeekBounds(value) {
  const d = parseLocalDate(value);
  if (!d) return getWeekBounds(new Date());
  const safeDate = Number.isNaN(d.getTime()) ? new Date() : d;
  const day = safeDate.getDay();
  const diff = safeDate.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(safeDate);
  monday.setDate(diff);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    start: formatDateInput(monday),
    end: formatDateInput(sunday)
  };
}

const MOOD_OPTIONS = [
  { score: 1, label: 'Very Low' },
  { score: 2, label: 'Low' },
  { score: 3, label: 'Neutral' },
  { score: 4, label: 'Good' },
  { score: 5, label: 'Excellent' }
];

const SUMMARY_FIELDS = [
  { key: 'keyAchievements', label: 'Key Achievements', placeholder: 'What did the team accomplish?' },
  { key: 'issuesBlockers', label: 'Issues / Blockers', placeholder: 'What is blocking progress?' },
  { key: 'supportRequired', label: 'Support Required', placeholder: 'What support does the team need?' },
  { key: 'nextWeekPlan', label: 'Next Week Plan', placeholder: 'What will be done next week?' }
];

const getMissionId = (mission) => mission?._id || mission || '';

const getMissionName = (summary, missions) => {
  if (summary?.mission?.name) return summary.mission.name;
  const missionId = getMissionId(summary?.mission);
  const match = missions.find(m => m._id === missionId);
  return match?.name || 'Mission unavailable';
};

const clampMood = (value) => {
  const score = Number(value);
  if (!Number.isFinite(score)) return 3;
  return Math.max(1, Math.min(5, Math.round(score)));
};

const sortSummaries = (items) => [...items].sort((a, b) => {
  const bDate = new Date(b.weekStartDate).getTime() || 0;
  const aDate = new Date(a.weekStartDate).getTime() || 0;
  return bDate - aDate;
});

const createDefaultForm = () => {
  const today = new Date();
  const bounds = getWeekBounds(today);
  return {
    mission: '',
    weekNumber: getWeekNumber(today),
    weekStartDate: bounds.start,
    weekEndDate: bounds.end,
    keyAchievements: '',
    issuesBlockers: '',
    supportRequired: '',
    nextWeekPlan: '',
    overallMoodScore: 3
  };
};

const summaryToForm = (summary) => ({
  mission: getMissionId(summary.mission),
  weekNumber: summary.weekNumber || getWeekNumber(summary.weekStartDate || new Date()),
  weekStartDate: formatDateInput(summary.weekStartDate),
  weekEndDate: formatDateInput(summary.weekEndDate),
  keyAchievements: summary.keyAchievements || '',
  issuesBlockers: summary.issuesBlockers || '',
  supportRequired: summary.supportRequired || '',
  nextWeekPlan: summary.nextWeekPlan || '',
  overallMoodScore: clampMood(summary.overallMoodScore)
});

const WeeklySummaryTab = ({ missions = [] }) => {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState('');
  const [form, setForm] = useState(createDefaultForm);
  const [aiRange, setAiRange] = useState({
    from: form.weekStartDate,
    to: form.weekEndDate
  });

  const loadSummaries = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const { data } = await axiosInstance.get('/api/vision/weekly-summaries');
      setSummaries(sortSummaries(data.data || []));
    } catch (err) {
      console.error(err);
      setLoadError('Failed to load weekly summaries.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSummaries(); }, [loadSummaries]);

  useEffect(() => {
    setAiRange({ from: form.weekStartDate, to: form.weekEndDate });
  }, [form.weekStartDate, form.weekEndDate]);

  const resetForm = () => {
    const nextForm = createDefaultForm();
    setForm(nextForm);
    setAiRange({ from: nextForm.weekStartDate, to: nextForm.weekEndDate });
    setAiError('');
    setShowAiPanel(false);
    setEditingId(null);
  };

  const openNewSummary = () => {
    resetForm();
    setShowForm(true);
  };

  const openEditSummary = (summary) => {
    const nextForm = summaryToForm(summary);
    setForm(nextForm);
    setAiRange({ from: nextForm.weekStartDate, to: nextForm.weekEndDate });
    setAiError('');
    setShowAiPanel(false);
    setEditingId(summary._id);
    setShowForm(true);
  };

  const closeForm = () => {
    resetForm();
    setShowForm(false);
  };

  const handleWeekChange = (startDate) => {
    const bounds = getWeekBounds(startDate);
    setForm(f => ({
      ...f,
      weekStartDate: bounds.start,
      weekEndDate: bounds.end,
      weekNumber: getWeekNumber(startDate)
    }));
  };

  const handleMissionChange = (value) => {
    setAiError('');
    setForm(f => ({
      ...f,
      mission: value,
      ...(editingId ? {} : {
        keyAchievements: '',
        issuesBlockers: '',
        supportRequired: '',
        nextWeekPlan: ''
      })
    }));
  };

  const handleAiRangeChange = (field, value) => {
    setAiRange(r => ({ ...r, [field]: value }));
    setAiError('');
  };

  const handleSubmit = async () => {
    if (!form.mission) { alert('Please select a mission.'); return; }
    if (!form.weekStartDate || !form.weekEndDate) { alert('Week dates are required.'); return; }

    const start = parseLocalDate(form.weekStartDate);
    const end = parseLocalDate(form.weekEndDate);
    if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
      alert('Please choose a valid week.');
      return;
    }

    setSaving(true);
    try {
      const request = editingId
        ? axiosInstance.put(`/api/vision/weekly-summaries/${editingId}`, form)
        : axiosInstance.post('/api/vision/weekly-summaries', form);
      const { data } = await request;
      const savedSummary = data.data;

      setSummaries(prev => {
        const withoutSaved = prev.filter(s => s._id !== savedSummary._id);
        return sortSummaries([savedSummary, ...withoutSaved]);
      });
      closeForm();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Failed to save summary.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (summary) => {
    const missionName = getMissionName(summary, missions);
    const confirmed = window.confirm(`Delete the Week ${summary.weekNumber} summary for ${missionName}?`);
    if (!confirmed) return;

    setDeletingId(summary._id);
    try {
      await axiosInstance.delete(`/api/vision/weekly-summaries/${summary._id}`);
      setSummaries(prev => prev.filter(s => s._id !== summary._id));
      if (editingId === summary._id) closeForm();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Failed to delete summary.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleGenerate = async () => {
    if (!form.mission) { setAiError('Select a mission first.'); return; }
    if (!aiRange.from || !aiRange.to) { setAiError('Select a date range first.'); return; }

    const from = parseLocalDate(aiRange.from);
    const to = parseLocalDate(aiRange.to);
    if (!from || !to || Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
      setAiError('Select a valid date range.');
      return;
    }
    const diff = (to - from) / (1000 * 60 * 60 * 24);
    if (diff < 0) { setAiError('From date must be before To date.'); return; }
    if (diff > 10) { setAiError('Date range cannot exceed 10 days.'); return; }

    setAiGenerating(true);
    setAiError('');
    try {
      const { data } = await axiosInstance.post('/api/vision/weekly-summaries/generate', {
        missionId: form.mission,
        fromDate: aiRange.from,
        toDate: aiRange.to,
      });
      const generated = data.data || {};
      setForm(f => ({
        ...f,
        keyAchievements: generated.keyAchievements || '',
        issuesBlockers: generated.issuesBlockers || '',
        supportRequired: generated.supportRequired || '',
        nextWeekPlan: generated.nextWeekPlan || '',
        overallMoodScore: clampMood(generated.overallMoodScore)
      }));
      setShowAiPanel(false);
    } catch (err) {
      console.error(err);
      setAiError(err.response?.data?.message || 'Generation failed. Try again.');
    } finally {
      setAiGenerating(false);
    }
  };

  const selectedMood = MOOD_OPTIONS.find(m => m.score === form.overallMoodScore) || MOOD_OPTIONS[2];
  const selectedMissionName = missions.find(m => m._id === form.mission)?.name || '';
  // Writes into a locked mission are rejected with a 423 — keep closed phases out
  // of the picker rather than letting the save fail after the form is filled in.
  const openMissions = missions.filter(m => !m.isLocked);

  return (
    <div>
      <div className="v26-section-header" data-tour="weekly-header">
        <span className="v26-section-title">Weekly Summary</span>
        <button className="v26-btn v26-btn--primary" type="button" onClick={openNewSummary}>
          <HiOutlinePlus size={16} /> New Summary
        </button>
      </div>

      {showForm && (
        <div className="v26-panel">
          <div className="v26-weekly-form-head">
            <div>
              <h3>{editingId ? 'Edit Weekly Summary' : 'New Weekly Summary'}</h3>
              <p>{selectedMissionName || 'Select a mission and week to continue.'}</p>
            </div>
            <button className="v26-weekly-icon-btn" type="button" onClick={closeForm} title="Close form">
              <HiOutlineX size={16} />
            </button>
          </div>

          <div className="v26-form">
            <div className="v26-form-group">
              <label>Mission *</label>
              <select value={form.mission} onChange={e => handleMissionChange(e.target.value)}>
                <option value="">{openMissions.length ? 'Select a mission...' : 'No unlocked missions — unlock one first'}</option>
                {openMissions.map(m => (
                  <option key={m._id} value={m._id}>{m.name}</option>
                ))}
              </select>
            </div>

            <div className="v26-form-row">
              <div className="v26-form-group">
                <label>Week Start *</label>
                <input type="date" value={form.weekStartDate} onChange={e => handleWeekChange(e.target.value)} />
              </div>
              <div className="v26-form-group">
                <label>Week End</label>
                <input type="date" value={form.weekEndDate} readOnly />
              </div>
              <div className="v26-form-group">
                <label>Week Number</label>
                <input value={`Week ${form.weekNumber}`} readOnly />
              </div>
            </div>

            <div className="v26-weekly-ai-box">
              <button
                type="button"
                className="v26-btn v26-btn--secondary"
                onClick={() => { setShowAiPanel(s => !s); setAiError(''); }}
              >
                <HiOutlineSparkles size={15} />
                {showAiPanel ? 'Hide AI Generate' : 'Generate with AI'}
              </button>

              {showAiPanel && (
                <div className="v26-weekly-ai-panel">
                  <p>AI will use the selected mission data inside this date range.</p>
                  <div className="v26-form-row">
                    <div className="v26-form-group">
                      <label>Data From</label>
                      <input
                        type="date"
                        value={aiRange.from}
                        onChange={e => handleAiRangeChange('from', e.target.value)}
                      />
                    </div>
                    <div className="v26-form-group">
                      <label>Data To</label>
                      <input
                        type="date"
                        value={aiRange.to}
                        onChange={e => handleAiRangeChange('to', e.target.value)}
                      />
                    </div>
                    <div className="v26-weekly-ai-action">
                      <button
                        type="button"
                        className="v26-btn v26-btn--primary"
                        onClick={handleGenerate}
                        disabled={aiGenerating}
                      >
                        <HiOutlineSparkles size={15} />
                        {aiGenerating ? 'Generating...' : 'Generate'}
                      </button>
                    </div>
                  </div>
                  {aiError && <p className="v26-weekly-error">{aiError}</p>}
                  {aiGenerating && <p className="v26-weekly-hint">Analysing mission data and preparing the summary...</p>}
                </div>
              )}
            </div>

            <div className="v26-form-group">
              <label>Team Mood</label>
              <div className="v26-mood v26-mood--weekly">
                {MOOD_OPTIONS.map(option => (
                  <button
                    key={option.score}
                    type="button"
                    className={`v26-mood-choice ${option.score === form.overallMoodScore ? 'is-active' : ''}`}
                    onClick={() => setForm(f => ({ ...f, overallMoodScore: option.score }))}
                    title={option.label}
                  >
                    {option.score}
                  </button>
                ))}
                <span>{selectedMood.label}</span>
              </div>
            </div>

            {SUMMARY_FIELDS.map(field => (
              <div className="v26-form-group" key={field.key}>
                <label>{field.label}</label>
                <textarea
                  value={form[field.key]}
                  onChange={e => setForm(f => ({ ...f, [field.key]: e.target.value }))}
                  rows={field.key === 'keyAchievements' ? 4 : 3}
                  placeholder={field.placeholder}
                />
              </div>
            ))}

            <div className="v26-weekly-form-actions">
              <button className="v26-btn v26-btn--secondary" type="button" onClick={closeForm}>
                <HiOutlineX size={15} /> Cancel
              </button>
              <button className="v26-btn v26-btn--primary" type="button" onClick={handleSubmit} disabled={saving}>
                <HiOutlineCheck size={15} />
                {saving ? 'Saving...' : editingId ? 'Update Summary' : 'Save Summary'}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="v26-empty">Loading summaries...</div>
      ) : loadError ? (
        <div className="v26-empty">{loadError}</div>
      ) : summaries.length === 0 ? (
        <div className="v26-empty">No weekly summaries yet. Add your first one above.</div>
      ) : (
        <div>
          {summaries.map(summary => {
            const mood = MOOD_OPTIONS.find(m => m.score === clampMood(summary.overallMoodScore)) || MOOD_OPTIONS[2];
            return (
              <div key={summary._id} className="v26-weekly-card">
                <div className="v26-weekly-card-head">
                  <div>
                    <span className="v26-weekly-mission">{getMissionName(summary, missions)}</span>
                    <span className="v26-weekly-week">
                      Week {summary.weekNumber} - {formatDisplayDate(summary.weekStartDate)} to {formatDisplayDate(summary.weekEndDate)}
                    </span>
                  </div>
                  <div className="v26-weekly-card-actions">
                    <span className="v26-weekly-mood-pill">Mood {mood.score}/5 - {mood.label}</span>
                    <button
                      className="v26-weekly-icon-btn"
                      type="button"
                      onClick={() => openEditSummary(summary)}
                      title="Edit summary"
                    >
                      <HiOutlinePencil size={14} />
                    </button>
                    <button
                      className="v26-weekly-icon-btn v26-weekly-icon-btn--danger"
                      type="button"
                      onClick={() => handleDelete(summary)}
                      disabled={deletingId === summary._id}
                      title="Delete summary"
                    >
                      <HiOutlineTrash size={14} />
                    </button>
                  </div>
                </div>

                <div className="v26-weekly-grid">
                  {SUMMARY_FIELDS.map(field => (
                    summary[field.key] ? (
                      <div className="v26-weekly-field" key={field.key}>
                        <label>{field.label}</label>
                        <p>{summary[field.key]}</p>
                      </div>
                    ) : null
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WeeklySummaryTab;
